import { CheckCircleFilled } from '@ant-design/icons';
import { Collapse } from 'antd';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/ChevronDown.svg';
import clsx from 'clsx';
import React from 'react';

interface CollapsibleCardProps {
  title: string;
  children?: React.ReactNode;
  isOpen?: boolean;
  showContent?: boolean;
  isComplete?: boolean;
  onToggle?: () => void;
}

export default function CollapsibleCard({
  title,
  children,
  isOpen = true,
  showContent = true,
  isComplete = false,
  onToggle,
}: CollapsibleCardProps) {
  const isInteractive = !!onToggle && showContent && isComplete;

  if (!showContent || (!isOpen && !isComplete)) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="px-4 py-2">
          <span className="text-sm text-gray-400">{title}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'rounded bg-white',
        isComplete
          ? 'border-green-200 !bg-vert-transport-1'
          : 'border border-gray-200',
      )}
    >
      <Collapse
        ghost
        bordered={false}
        activeKey={isOpen ? ['content'] : []}
        collapsible={isInteractive ? 'header' : 'disabled'}
        onChange={() => onToggle?.()}
        expandIcon={() => null}
        className={clsx(
          'bg-transparent [&_.ant-collapse-header]:!px-0 [&_.ant-collapse-header]:!py-1 [&_.ant-collapse-content-box]:bg-white [&_.ant-collapse-content-box]:rounded-b [&_.ant-collapse-header-text]:w-full',
          isComplete &&
            '[&_.ant-collapse-content-box]:border-[1px] [&_.ant-collapse-content-box]:border-color-border [&_.ant-collapse-content-box]:rounded',
        )}
        items={[
          {
            key: 'content',
            label: (
              <div
                className={clsx(
                  'flex w-full items-center justify-between px-4 py-2 text-sm font-medium',
                  !isComplete && 'border-b-[1px] border-color-border',
                  isComplete ? 'text-green-700' : 'text-bleu-hellio-500',
                  isInteractive ? 'cursor-pointer' : 'cursor-default',
                )}
              >
                <div className="flex min-w-0 items-center gap-2">
                  {isComplete && (
                    <CheckCircleFilled className="shrink-0 text-green-600" />
                  )}
                  <span>{title}</span>
                </div>
                {isComplete && (
                  <ChevronDownIcon
                    className={clsx(
                      'ml-2 shrink-0 text-green-600 transition-transform',
                      isOpen ? 'rotate-180' : 'rotate-0',
                    )}
                    aria-hidden="true"
                  />
                )}
              </div>
            ),
            children,
          },
        ]}
      />
    </div>
  );
}
